/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { ArrowRightOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Input, Result, Space, Spin } from 'antd'
import { debounce } from 'lodash'
import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { IGNORE_ERR_HEADER } from '@/constants'
import { requiredRule } from '@/formRules/baseRule'
import { useTitle } from '@/hooks/useTitle'
import { CloudLabelWithIcon } from '@/pages/cloud/features/CloudLabelWithIcon'
import { CloudProvider } from '@/pages/cloud/interface'
import styles from '@/pages/contract/Contracts/CreateContractModal/index.less'
import { ContractSelect } from '@/pages/contract/features/ContractSelect'
import { MarketPlaceState } from '@/pages/marketplace/interface'
import { serverEmail } from '@/process.env'
import { CheckVouchers } from '@/routes/RouteGuard/CheckVouchers'
import { accountClient } from '@/services/cloudClient/accountClient'
import { axiosInstance } from '@/services/cloudClient/axiosInstance'
import { ErrorReason } from '@/services/interface'
import { getQueryParam } from '@/utils/getQueryParam'

// 判断当前环境（US 或 Global）
function getCurrentRegion(): 'us' | 'global' {
  const hostname = window.location.hostname
  if (hostname.includes('ap-app')) return 'global'
  return 'us' // 默认 US
}

// AWS Marketplace 链接配置
const MARKETPLACE_LINKS = {
  us: {
    privateOffer: 'https://aws.amazon.com/marketplace/customer-connect/private-offer/prodview-ofzyfzpx52yni?campaignId=701RU00000InQAHYA3',
    paidOffer: 'https://aws.amazon.com/marketplace/procurement/?productId=prod-ishfrpi7vd7hy&offerID=offer-s2iyb3wr4sl2e',
  },
  global: {
    privateOffer: '', // 暂时为空，等待 jack 上线
    paidOffer: 'https://aws.amazon.com/marketplace/procurement/?productId=prod-ddx25vqoy7s7m&offerId=offer-p4zmkpzdoxss2',
  },
}

interface TryLinkMarketplaceResponse {
  code: number
  message: string
}

export function Marketplace(props: React.PropsWithRef<HTMLAttributes<any>>) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const paramsToken = getQueryParam('token')
  const cloudProvider = getQueryParam('cloud_provider')
  const [loading, setLoading] = useState(false)
  const [statusFailedMessage, setStatusFailedMessage] = useState<any>()
  const [activeFailedMessage, setActiveFailedMessage] = useState<any>()
  const [success, setSuccess] = useState(false)
  const [constractId, setConstractId] = useState()
  const [initialLoading, setInitialLoading] = useState(false)
  const [contractOptions, setContractOptions] = useState<any>([])
  const [linkValidationStatus, setLinkValidationStatus] = useState<TryLinkMarketplaceResponse | null>(null)
  const [linkValidationLoading, setLinkValidationLoading] = useState(false)
  const formToken = Form.useWatch('token', form)
  const formContractId = Form.useWatch('contractId', form)
  useTitle(t('Link Marketplace'))

  // 判断是否允许链接
  const canLinkMarketplace = linkValidationStatus === null || linkValidationStatus.code === 0

  // 调用 tryLinkMarketplace API
  const tryLinkMarketplace = useCallback(async (contractId: string) => {
    if (!contractId) {
      setLinkValidationStatus(null)
      return
    }

    setLinkValidationLoading(true)
    try {
      const response = await axiosInstance.get<TryLinkMarketplaceResponse>(`/api/v1/contracts/${contractId}/tryLinkMarketplace`, IGNORE_ERR_HEADER)
      setLinkValidationStatus(response.data)
    } catch (error: any) {
      // 如果 API 调用失败，允许继续链接（保持原有行为）
      console.error('Failed to check link marketplace status:', error)
      setLinkValidationStatus(null)
    } finally {
      setLinkValidationLoading(false)
    }
  }, [])

  const onFinish = (body: any) => {
    const { contractId, token } = body
    setLoading(true)

    accountClient.marketplaceApi
      .activateMarketplace(contractId, { token, cloudProvider }, IGNORE_ERR_HEADER)
      .then(() => {
        setConstractId(contractId)
        setSuccess(true)
      })
      .catch(err => {
        if (err.response.data?.reason === ErrorReason.NOT_IMPLEMENTED) {
          setActiveFailedMessage(t('You can only resubscribe after 1 hour of unsubscribing.'))
        } else {
          setActiveFailedMessage(err.response.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  const debouncedCheckRef = useRef(
    debounce((token: string) => {
      if (token && cloudProvider) {
        setInitialLoading(true)
        accountClient.marketplaceApi
          .getMarketplaceStatus({ token, cloudProvider }, IGNORE_ERR_HEADER)
          .then(res => {
            if (res.data.status == MarketPlaceState.Subscribed) {
              setStatusFailedMessage('')
            } else if (res.data.status == MarketPlaceState.Activated) {
              setStatusFailedMessage(
                <Space>
                  {t('used_token_desc')}
                  <Link to={`/contracts`}>
                    <Button type="primary" size="small" icon={<ArrowRightOutlined />}>
                      {t('Go to Contracts')}
                    </Button>
                  </Link>
                </Space>,
              )
            } else if (res.data.status == MarketPlaceState.Unsubscribed) {
              setStatusFailedMessage(t('unsubscribed_token_desc') as string)
            } else {
              setStatusFailedMessage(t('waiting_token_status') as string)
            }
          })
          .catch(err => {
            // 云厂商报错
            if (err.response.data?.reason === ErrorReason.INVALID_PARAMETER) {
              setStatusFailedMessage(t('Token is Invalid'))
            } else {
              setStatusFailedMessage(err.response.data?.message)
            }
          })
          .finally(() => setInitialLoading(false))
      } else if (!cloudProvider) {
        setStatusFailedMessage(t('Cloud provider is required'))
      } else {
        setStatusFailedMessage(t('Token is required'))
      }
    }, 500),
  )

  useEffect(() => {
    debouncedCheckRef.current(formToken)
    return () => {
      debouncedCheckRef.current.cancel()
    }
  }, [window.location.search, formToken])

  // 监听 contract 选择变化，调用验证 API
  useEffect(() => {
    if (formContractId && !statusFailedMessage) {
      tryLinkMarketplace(formContractId)
    } else {
      setLinkValidationStatus(null)
    }
  }, [formContractId, statusFailedMessage, tryLinkMarketplace])

  return (
    <div className="marketplace-container common-card" {...props}>
      <h1 style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
        <Trans i18nKey="activate_account">
          Link <CloudLabelWithIcon cloud={cloudProvider as CloudProvider} /> marketplace
        </Trans>
      </h1>
      <CheckVouchers
        finishedCallback={() => location.reload()}
        emptyContent={
          <Result
            icon={null}
            subTitle={
              <>
                <div style={{ marginBottom: '16px' }}>{t('sales_contact')}</div>
                <div className={styles.footer}>
                  <div>Email：{serverEmail}</div>
                </div>
              </>
            }
          />
        }
      >
        {success ? (
          <Result
            status="success"
            title={t('Authorization linked successfully')}
            subTitle={contractOptions?.find((item: any) => item.value == constractId)?.tags}
            extra={
              <Link to={`/contracts/${constractId}/contextlakes`}>
                <Button type="primary">{t('Go to Contract')}</Button>
              </Link>
            }
          />
        ) : (
          <Spin spinning={initialLoading}>
            <Form form={form} onFinish={onFinish} onFinishFailed={() => setLoading(false)} layout="vertical" initialValues={{ token: paramsToken }}>
              {statusFailedMessage && <Alert message={statusFailedMessage} type="error" style={{ marginBottom: '16px' }} />}
              {activeFailedMessage && <Alert message={activeFailedMessage} type="error" style={{ marginBottom: '16px' }} />}
              <Form.Item label={t('Token')} name="token" rules={[requiredRule()]} style={{ display: paramsToken ? 'none' : 'block' }}>
                <Input placeholder={t('marketplace_token_placeholder')} size="large" />
              </Form.Item>
              {!statusFailedMessage && (
                <>
                  <Form.Item name="contractId" label={t('Contract')} rules={[requiredRule()]}>
                    <ContractSelect
                      withMarketplace={true}
                      allowCreate={false}
                      size="large"
                      optionDisabled={item => item.marketplace?.state === 'activated'}
                      onLoaded={setContractOptions}
                    />
                  </Form.Item>
                  {linkValidationStatus && linkValidationStatus.code < 0 && (
                    <Alert
                      message={linkValidationStatus.code === -3 ? 'Free Trial unavailable for this contract' : linkValidationStatus.message}
                      description={
                        linkValidationStatus.code === -3 ? (
                          <div>
                            <p style={{ marginBottom: '12px' }}>
                              This contract doesn't qualify for the Free Trial. You can continue by subscribing to a paid offer on AWS Marketplace, or
                              request a private offer from our sales team.
                            </p>
                            <Space>
                              {(() => {
                                const region = getCurrentRegion()
                                const links = MARKETPLACE_LINKS[region]
                                return (
                                  <>
                                    {links.privateOffer && (
                                      <Button type="primary" onClick={() => window.open(links.privateOffer, '_blank')}>
                                        Request private offer
                                      </Button>
                                    )}
                                    <Button type="primary" onClick={() => window.open(links.paidOffer, '_blank')}>
                                      Upgrade to a paid subscription
                                    </Button>
                                  </>
                                )
                              })()}
                            </Space>
                          </div>
                        ) : undefined
                      }
                      type="error"
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                  )}
                  <Alert
                    message={t('Contract Creation Notice')}
                    description={
                      <div>
                        <p>{t('marketplace_contract_notice')}</p>
                        <p style={{ marginTop: '8px', marginBottom: '0' }}>
                          <strong>Email: {serverEmail}</strong>
                        </p>
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />
                  <Form.Item>
                    <Button
                      block
                      type="primary"
                      size="large"
                      htmlType="submit"
                      loading={loading || linkValidationLoading}
                      disabled={!canLinkMarketplace}
                    >
                      {t('Link Marketplace')}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form>
          </Spin>
        )}
      </CheckVouchers>
    </div>
  )
}
